import {
  ALL_FEATURE_FLAGS,
  CHAT_KILL_SWITCH_FLAG,
  DUAL_REP_FLAG,
  FINANCIALS_MVP_FLAG,
  PORTAL_SECTION_FLAGS,
  type PortalSectionFlag,
} from "@shared/constants/featureFlags";
import { ref } from "vue";

import { fetchFeatureFlags } from "@/api/featureFlags";

/**
 * Reactive feature-flag cache backed by Mixpanel feature gates.
 *
 * Flags are evaluated SERVER-SIDE by RAP's `GET /feature-flags` (RAP-668), not by the browser
 * Mixpanel SDK: an ad blocker blocks Mixpanel, and with every gate failing closed a funder saw an
 * empty portal. The server derives the targeting context (user id, `firm_id`, `firm_name`,
 * `user_kind`, `handled_staff`) from the authenticated session. `handled_staff` (RAP-801) is true
 * for Handled staff, including while they impersonate a customer, so one Mixpanel rule
 * `handled_staff = true → On` opens a flag to every staff member; nothing here sends or sets it.
 * The cache is a Vue ref so router `canAccess` guards
 * and the sidebar read it synchronously and re-render once the answer lands.
 *
 * The keys themselves live in `@shared/constants/featureFlags` — the backend evaluates only those.
 */
export { CHAT_KILL_SWITCH_FLAG, DUAL_REP_FLAG, FINANCIALS_MVP_FLAG, PORTAL_SECTION_FLAGS };
export type { PortalSectionFlag };

/*
 * PORTAL_SECTION_FLAGS — one gate per referring-portal section, so a section can be switched on
 * or off without a deploy. Detail screens ride their list's flag (Lead Detail → `leads`, Case
 * Detail → `cases`) — a detail page reachable while its list is hidden would be a section that is
 * only half off.
 *
 * Fails closed like every flag here: a section whose gate is missing from Mixpanel, or unresolved
 * because Mixpanel is unreachable, is hidden. Each gate therefore has to exist and be on in BOTH the
 * prod and the dev Mixpanel projects before that section shows anywhere.
 *
 * CHAT_KILL_SWITCH_FLAG (`ai_chat_disabled`) — chat is hidden for a user when this gate is ON.
 *
 * Propagation: an open tab picks a flip up on its next navigation once the cached answer is older
 * than `FLAG_STALE_MS` (see `refreshFeatureFlagsIfStale`, which re-asks for THIS flag only), or on
 * reload / re-login — so within a few minutes of active use, not instantly. The server also caches
 * an answer for up to a minute.
 *
 * Deliberately the opposite polarity to every other flag here. Chat is already live, so it must
 * keep working when the gate is missing from a Mixpanel project or Mixpanel is unreachable — a
 * fail-closed "chat enabled" gate would switch chat off for everyone in those cases.
 * Target it by `user_kind` (handling / referring / funder / external …), `firm_id`, or user.
 * NEVER give it a `handled_staff` rule: ON here switches chat OFF for every staff member.
 */

/**
 * What kind of user this is, for flag targeting (`user_kind`). The backend derives the value it
 * sends Mixpanel itself (lib/analytics/feature-flags.ts); this client-side mirror only decides
 * when a re-fetch is due. The named values are the documented ones; any other org type comes
 * through lower-cased (`marketing`, `admin`, …).
 */
export type FlagUserKind = "external" | "funder" | "handling" | "referring" | (string & {});

/** Every flag refreshFeatureFlags() resolves — one request covers all. */
const ALL_FLAGS: readonly string[] = ALL_FEATURE_FLAGS;

/** Local-dev overrides so features are reachable without a Mixpanel token. */
const LOCAL_OVERRIDE_KEY = "rf-financials-enabled";
const DUAL_REP_OVERRIDE_KEY = "rf-dual-rep-enabled";

const flagState = ref<Record<string, boolean>>({});

/**
 * Settles once the first refresh for the current session has resolved. Portal routes are gated by
 * flags, and the guard is otherwise synchronous — without this wait, a reload on /portal/capital
 * would be judged against an empty cache and bounced before the flags had answered.
 */
let flagsReady: Promise<void>;
let markFlagsReady: () => void = () => undefined;
function armFlagsReady(): void {
  flagsReady = new Promise((resolve) => {
    markFlagsReady = resolve;
  });
}
armFlagsReady();

/** Upper bound on how long navigation waits for flags before deciding on what it has. */
const FLAGS_READY_TIMEOUT_MS = 3000;

// Bumped on every refresh and on reset. An in-flight refresh that resolves
// after a logout (or after a newer refresh) compares epochs and skips its
// write-back, so a stale user's flag can't leak into the next session.
let refreshEpoch = 0;

function hasLocalOverride(key: string): boolean {
  try {
    return localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
}

/**
 * Generic synchronous read. The local override key is derived from the flag key —
 * `portal_capital` → `rf-portal-capital-enabled` — so every new flag gets one without another
 * hand-written getter.
 */
export function isFeatureFlagEnabled(key: string): boolean {
  return flagState.value[key] === true || hasLocalOverride(overrideKeyFor(key));
}

export function overrideKeyFor(key: string): string {
  return `rf-${key.replace(/_/g, "-")}-enabled`;
}

/** True when the chat kill switch is ON for this user (chat hidden). Off — chat shown — when
 * the gate is missing or Mixpanel is unreachable. */
export function isChatKilledByFlag(): boolean {
  return isFeatureFlagEnabled(CHAT_KILL_SWITCH_FLAG);
}

/** Resolves when the session's first flag refresh has landed, or after a short timeout. */
export function whenFeatureFlagsReady(): Promise<void> {
  return Promise.race([
    flagsReady,
    new Promise<void>((resolve) => setTimeout(resolve, FLAGS_READY_TIMEOUT_MS)),
  ]);
}

/** Synchronous read for router guards / nav. Local override always wins so a
 * dev can force the feature on without analytics configured. */
export function isFinancialsMvpEnabled(): boolean {
  return flagState.value[FINANCIALS_MVP_FLAG] === true || hasLocalOverride(LOCAL_OVERRIDE_KEY);
}

/** Synchronous read for the Dual Rep router guard. "FeatureFlag" is spelled
 * out to disambiguate from the auth store's `isDualRepEnabled` (the org-level
 * beta setting) — the canAccess gate ANDs the two. */
export function isDualRepFeatureFlagEnabled(): boolean {
  return flagState.value[DUAL_REP_FLAG] === true || hasLocalOverride(DUAL_REP_OVERRIDE_KEY);
}

/** Re-evaluate all flags for the signed-in user in a single request. Safe to
 * fire-and-forget; updates the reactive cache when it resolves.
 *
 * Marks flags ready on EVERY outcome, failure included: before RAP-668 only a
 * successful refresh did, so a blocked or failed fetch made every navigation
 * wait out the full FLAGS_READY_TIMEOUT_MS. A refresh superseded by a logout
 * or a newer refresh neither writes nor marks — the readiness promise it would
 * settle belongs to the session that superseded it. */
export async function refreshFeatureFlags(): Promise<void> {
  const epoch = ++refreshEpoch;
  lastRefreshAt = Date.now();
  // `fetchFeatureFlags` never rejects, but the guard keeps a future change to it from leaving
  // navigation hanging on the readiness timeout again.
  let flags: Record<string, boolean>;
  try {
    flags = await fetchFeatureFlags(ALL_FLAGS);
  } catch {
    flags = Object.fromEntries(ALL_FLAGS.map((key) => [key, false]));
  }
  if (epoch !== refreshEpoch) return; // superseded by a logout or a newer refresh
  flagState.value = { ...flagState.value, ...flags };
  markFlagsReady();
}

/** How old a flag answer may get before a navigation re-asks the server. */
export const FLAG_STALE_MS = 5 * 60 * 1000;

/** When the current session last asked for flags; 0 = not yet (or logged out). */
let lastRefreshAt = 0;

/**
 * Flags re-asked mid-session. ONLY fail-open kill switches: a failed fetch answers every key
 * `false` (fetchFeatureFlags fails closed), which for a kill switch means "feature stays on" — but
 * for a fail-closed gate (the portal sections) it would strip a working user's nav on a Wi-Fi
 * blip. Those keep their once-per-session answer from identify.
 */
const MID_SESSION_FLAGS: readonly string[] = [CHAT_KILL_SWITCH_FLAG];

/**
 * Re-resolve the kill switches if the cached answer is older than `FLAG_STALE_MS` — called on
 * every route change, so a switch flipped in Mixpanel reaches long-lived tabs. A no-op before the
 * session's first refresh (identify owns that one) and after logout. It never supersedes a full
 * refresh (no epoch bump), and a logout or newer full refresh discards its answer.
 */
export function refreshFeatureFlagsIfStale(now = Date.now()): void {
  if (lastRefreshAt === 0 || now - lastRefreshAt < FLAG_STALE_MS) return;
  lastRefreshAt = now;
  const epoch = refreshEpoch;
  void fetchFeatureFlags(MID_SESSION_FLAGS).then((flags) => {
    if (epoch !== refreshEpoch) return;
    flagState.value = { ...flagState.value, ...flags };
  });
}

/** Clear cached flags on logout, and invalidate any in-flight refresh. */
export function resetFeatureFlags(): void {
  refreshEpoch++;
  lastRefreshAt = 0;
  flagState.value = {};
  armFlagsReady();
}
